import { useEffect, useRef, useSyncExternalStore, useState, useMemo } from "react";
import { SOCKET_MESSAGE_TYPES } from "@/other/constants/OtherKeys";
import { WEBSOCKET_ACTION_KEY } from "@/env";
import { useSwivuBryntumContext } from "./useSwivuBryntumContext";
import useViews from "./useViews.jsx";
import type { IAuthUser } from "@/types/AuthUser";

const ACTIVITY_INTERVAL = 5000;
const BROADCAST_INTERVAL = 10000;

interface Activity {
  userId: number;
  entityId?: number;
  entityType?: string;
  viewType?: string;
  lastActiveTime: number;
}

interface BoardUser {
  id: number;
  name: string;
  profileImageURL?: string;
}

interface CurrentMode {
  isEntity: boolean;
  isView: boolean;
  entityId?: number;
  entityType?: string;
  viewType?: string;
}

export const useLiveUsers = (): BoardUser[] => {
  const { user, project, swivuWebSocketRef, boardSessionIdRef, entityManager } = useSwivuBryntumContext();
  const viewManagerState = useViews();

  const otherUserActivityRef = useRef<Activity[]>([]);
  const [activeUsers, setActiveUsers] = useState<BoardUser[]>([]);

  // Memoize current user ID
  // Type assertion: user object has id property but IAuthUser interface is incomplete
  const currentUserId = useMemo(() => {
    const userWithId = user as IAuthUser;
    return userWithId?.id ? Number(userWithId.id) : null;
  }, [user]);

  // Determine current mode - memoized to avoid recalculation
  const currentMode = useMemo<CurrentMode>(() => {
    const { isOpen, entityId, entityType } = entityManager
    if (isOpen && entityId && entityType) {
      return {
        isEntity: true,
        isView: false,
        entityId: Number(entityId),
        entityType: entityType,
      };
    } else if (viewManagerState?.selectedView?.viewType) {
      return {
        isEntity: false,
        isView: true,
        viewType: viewManagerState.selectedView.viewType,
      };
    }
    return { isEntity: false, isView: false };
  }, [entityManager, viewManagerState]);

  const isSocketConnected = useSyncExternalStore(
    (onStoreChange) => {
      const socket = swivuWebSocketRef?.current;
      if (!socket) return () => {};
      return socket.renderSubscribe(onStoreChange);
    },
    () => {
      const socket = swivuWebSocketRef?.current;
      return socket ? socket.renderSnapShot() : false;
    }
  );

  // Helper: Check if activity matches current surface
  const isActivityMatch = (activity: Activity): boolean => {
    if (!currentUserId || activity.userId === currentUserId) return false;

    if (currentMode.isEntity) {
      return (
        activity.entityType === currentMode.entityType &&
        activity.entityId === currentMode.entityId
      );
    }

    if (currentMode.isView) {
      return activity.viewType === currentMode.viewType;
    }

    return false;
  };

  // Helper: Check if two activities represent the same user on same surface
  const isSameActivity = (a: Activity, b: Activity): boolean => {
    if (a.userId !== b.userId) return false;

    // Entity activities match by entityId + entityType
    if (a.entityId && a.entityType && b.entityId && b.entityType) {
      return a.entityType === b.entityType && a.entityId === b.entityId;
    }

    // View activities match by viewType
    if (a.viewType && b.viewType) {
      return a.viewType === b.viewType;
    }

    return false;
  };

  // Helper: Map activities to BoardUser objects
  const mapToBoardUsers = (activities: Activity[]): BoardUser[] => {
    if (!project) return [];

    return activities
      .map((activity): BoardUser | null => {
        const boardUser = project.boardUsersStore.find((record) => record.userId === activity.userId);
        return boardUser
          ? { id: boardUser.id, name: boardUser.name, profileImageURL: boardUser.profileImageURL }
          : null;
      })
      .filter((user): user is BoardUser => user !== null);
  };

  const loadActiveUsers = () => {
    if (!currentUserId || !project || (!currentMode.isEntity && !currentMode.isView)) {
      setActiveUsers([]);
      return;
    }

    const filteredActivities = otherUserActivityRef.current.filter(isActivityMatch);
    const boardUsers = mapToBoardUsers(filteredActivities);
    setActiveUsers(boardUsers);
  };

  const broadcastUserActivity = () => {
    const socket = swivuWebSocketRef?.current;
    const sessionId = boardSessionIdRef?.current;

    if (!isSocketConnected || !socket || !currentUserId || !sessionId) {
      return;
    }

    if (!currentMode.isEntity && !currentMode.isView) {
      return;
    }

    const activity = currentMode.isEntity
      ? { entityId: currentMode.entityId, entityType: currentMode.entityType, userId: currentUserId }
      : { viewType: currentMode.viewType, userId: currentUserId };

    socket.sendMessage({
      [WEBSOCKET_ACTION_KEY]: SOCKET_MESSAGE_TYPES.ACTIVITY,
      board_session_id: sessionId,
      activity,
    });
  };

  const handleOtherUserActivity = ({ data }: { data: string }) => {
    try {
      const parsed = JSON.parse(data);
      const { [WEBSOCKET_ACTION_KEY]: action, board_session_id, activity } = parsed;
      const currentSessionId = boardSessionIdRef?.current;

      if (action === SOCKET_MESSAGE_TYPES.ACTIVITY && board_session_id !== currentSessionId && activity) {
        const lastActiveTime = new Date().getTime();
        const isExisting = otherUserActivityRef.current.find((record) => isSameActivity(record, activity));

        if (isExisting) {
          isExisting.lastActiveTime = lastActiveTime;
        } else {
          otherUserActivityRef.current.push({ ...activity, lastActiveTime });
        }
      }
    } catch (error) {
      console.error("Error parsing websocket message:", error);
    }
  };

  const cleanUpInactiveUsers = () => {
    const currentTime = new Date().getTime();
    otherUserActivityRef.current = otherUserActivityRef.current.filter(
      (activity) => currentTime - activity.lastActiveTime < BROADCAST_INTERVAL
    );
  };

  // Set up websocket listener
  useEffect(() => {
    const currentSocket = swivuWebSocketRef?.current;
    if (isSocketConnected && currentSocket) {
      currentSocket.attachListenerToSocket("message", handleOtherUserActivity);
    }
  }, [isSocketConnected]);

  // Set up broadcasting and cleanup intervals
  useEffect(() => {
    if (!currentUserId || (!currentMode.isEntity && !currentMode.isView)) {
      setActiveUsers([]);
      return;
    }

    // Broadcast and load active users immediately when surface changes
    broadcastUserActivity();
    loadActiveUsers();

    // Set up intervals for periodic updates
    const activityInterval = setInterval(loadActiveUsers, ACTIVITY_INTERVAL);
    const cleanUpInterval = setInterval(() => {
      cleanUpInactiveUsers();
      broadcastUserActivity();
    }, BROADCAST_INTERVAL);

    return () => {
      clearInterval(activityInterval);
      clearInterval(cleanUpInterval);
    };
  }, [currentMode, currentUserId, isSocketConnected]);

  return activeUsers;
};
